import { useCallback, useEffect, useMemo, useRef } from 'react';

interface UsePopOverOpenWithDelayOptions {
  onOpenChange: (newOpen: boolean) => void;
  longHoverDelay?: boolean;
}

const usePopOverOpenWithDelay = ({
  onOpenChange,
  longHoverDelay,
}: UsePopOverOpenWithDelayOptions) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openDelay = longHoverDelay ? 500 : 200;
  const closeDelay = 100;

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const setHoverIntent = useCallback(
    (newOpen: boolean, delay: number) => {
      cancel();
      timeoutRef.current = setTimeout(() => {
        onOpenChange(newOpen);
      }, delay);
    },
    [cancel, onOpenChange],
  );

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  return useMemo(
    () => ({
      openPopOver: () => {
        setHoverIntent(true, openDelay);
      },
      closePopOver: ({ immediate }: { immediate?: boolean } = {}) => {
        setHoverIntent(false, immediate ? 0 : closeDelay);
      },
      cancel,
    }),
    [setHoverIntent, openDelay, closeDelay, cancel],
  );
};

export default usePopOverOpenWithDelay;
